import { memo, type ReactNode } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import {
  communicationsSubsectionAccordionDetailsSx,
  communicationsSubsectionAccordionSummarySx,
  communicationsSubsectionAccordionSx,
  communicationsSubsectionIconBoxSx,
} from "./communicationsSubsectionStyles";

export interface CommunicationsSubsectionPanelProps {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  children: ReactNode;
  /** Estado inicial do accordion (padrão: expandido). */
  defaultExpanded?: boolean;
  /** Identificador para acessibilidade (aria-controls / id). */
  panelId?: string;
}

/**
 * Subseção colapsável da Gestão de comunicações (FluxZap), alinhada ao SectionHeader.
 */
const CommunicationsSubsectionPanel = memo(
  ({
    icon,
    title,
    subtitle,
    children,
    defaultExpanded = true,
    panelId = "communications-subsection",
  }: CommunicationsSubsectionPanelProps) => {
    const headerId = `${panelId}-header`;
    const contentId = `${panelId}-content`;

    return (
      <Accordion
        defaultExpanded={defaultExpanded}
        disableGutters
        elevation={0}
        sx={communicationsSubsectionAccordionSx}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={contentId}
          id={headerId}
          sx={communicationsSubsectionAccordionSummarySx}
        >
          <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ width: "100%" }}>
            <Paper variant="outlined" sx={communicationsSubsectionIconBoxSx}>
              {icon}
            </Paper>
            <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1, pr: 0.5 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {title}
              </Typography>
              {subtitle ? (
                <Typography variant="caption" color="text.secondary">
                  {subtitle}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
        </AccordionSummary>

        <AccordionDetails id={contentId} sx={communicationsSubsectionAccordionDetailsSx}>
          {children}
        </AccordionDetails>
      </Accordion>
    );
  },
);

CommunicationsSubsectionPanel.displayName = "CommunicationsSubsectionPanel";

export default CommunicationsSubsectionPanel;
